import { Link } from "react-router-dom";
import { Sprout, BookOpen, HeartHandshake, Users2, ArrowRight, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { fileUrl } from "@/lib/api";
import Footer from "@/components/Footer";

const HERO = "https://images.pexels.com/photos/8101984/pexels-photo-8101984.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Landing() {
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen bg-bizma-cream text-bizma-ink" data-testid="landing-page">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-bizma-cream/80 border-b border-bizma-border">
        <div className="container-x flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-bizma.png" alt="BIZMA" className="w-9 h-9 rounded-full object-cover shadow-sm" />
            <div className="font-display font-bold text-xl tracking-wider">BIZMA</div>
          </Link>
          <div className="flex items-center gap-2">
            {user ? (
              <>
                <Link to="/app/me" className="flex items-center gap-2 hover:bg-bizma-muted rounded-full px-2 py-1 transition-colors" data-testid="landing-user">
                  {user.photo_path ? (
                    <img src={fileUrl(user.photo_path)} alt="" className="w-9 h-9 rounded-full object-cover border-2 border-bizma-sage" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-bizma-sage text-white grid place-items-center font-semibold text-sm">
                      {(user.name || "?").charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span className="hidden sm:inline text-sm font-medium">{user.name}</span>
                </Link>
                <Link to="/app" className="btn-primary text-sm py-2 px-4" data-testid="landing-open-app">Buka Aplikasi</Link>
                <button onClick={logout} className="text-bizma-subtext hover:text-bizma-terracotta p-2 transition-colors" title="Keluar" data-testid="landing-logout"><LogOut size={18}/></button>
              </>
            ) : (
              <>
                <Link to="/login" className="btn-outline text-sm py-2 px-4" data-testid="header-login-btn">Masuk</Link>
                <Link to="/register" className="btn-primary text-sm py-2 px-4" data-testid="header-register-btn">Daftar</Link>
              </>
            )}
          </div>
        </div>
      </header>

      <section className="container-x py-16 sm:py-24 grid lg:grid-cols-2 gap-12 items-center">
        <div className="animate-fade-in">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-bizma-sage mb-4">Komunitas Pelaku Usaha Magelang</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
            Grow <span className="text-bizma-terracotta">Together</span>.<br/>Tumbuh bersama BIZMA.
          </h1>
          <p className="mt-6 text-lg text-[#4A4743] leading-relaxed max-w-xl">
            Direktori anggota dan bisnis untuk saling kenal, saling dukung, dan tumbuh bersama.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register" className="btn-primary" data-testid="hero-cta-register">
              Daftar Sekarang <ArrowRight size={18} />
            </Link>
            <Link to="/login" className="btn-outline" data-testid="hero-cta-login">Sudah punya akun? Masuk</Link>
          </div>
        </div>
        <div className="relative rounded-3xl overflow-hidden border border-bizma-border shadow-xl">
          <img src={HERO} alt="Community" className="w-full h-[420px] object-cover" />
        </div>
      </section>

      <section className="container-x py-16" data-testid="benefits-section">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-bizma-sage mb-3">Kenapa Gabung Bizma?</div>
          <h2 className="text-3xl sm:text-4xl font-display font-bold">Tiga manfaat utama menjadi anggota</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          <BenefitCard icon={<BookOpen size={28}/>} title="Belajar Bareng" desc="Diskusi rutin, sharing pengalaman, dan artikel blog dari sesama pelaku usaha untuk saling upgrade skill." testId="benefit-belajar" />
          <BenefitCard icon={<HeartHandshake size={28}/>} title="Saling Mendukung" desc="Dukungan komunitas dari pemula sampai yang sudah scaling — kita tumbuh bareng, bukan sendirian." testId="benefit-mendukung" />
          <BenefitCard icon={<Users2 size={28}/>} title="Tambah Relasi" desc="Jejaring dengan pelaku usaha Magelang lintas kategori — kuliner, jasa, fashion, teknologi, dan lainnya." testId="benefit-relasi" />
        </div>
      </section>

      <footer className="border-t border-bizma-border">
        <div className="container-x py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-bizma-subtext">
          <div>© {new Date().getFullYear()} BIZMA. Grow Together.</div>
          <div>Komunitas Pelaku Usaha Magelang</div>
        </div>
      </footer>
    </div>
  );
}
function BenefitCard({ icon, title, desc, testId }) {
  return (
    <div className="felt-card p-8 transition-transform hover:-translate-y-1" data-testid={testId}>
      <div className="w-14 h-14 rounded-2xl bg-bizma-terracotta/10 text-bizma-terracotta grid place-items-center mb-5">{icon}</div>
      <h3 className="text-xl font-display font-semibold">{title}</h3>
      <p className="mt-2 text-[#4A4743] leading-relaxed">{desc}</p>
    </div>
  );
}
